import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { Button } from '@startuptools/ui/Button';
import { Trans, useLingui } from '@lingui/react';
import {
  useAgSetSnrCaseIdMutation,
  useCreateAuthorizationGroupMutation,
  useDeleteAuthorizationGroupMutation,
} from '../../graphql/react-operations';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { AuthorizationsDocument, useAuthorizationsSuspenseQuery } from './Authorizations.graphql';
import { DateFormat } from '@startuptools/ui/DateFormat';
import { Fragment, useState, type MouseEvent } from 'react';
import { GqlAuthKind, GqlSnrCaseSubjectKind } from '../../graphql/base-types.graphql';
import { ConnectSnrCaseDialog } from '../../components/connect-snr-case-dialog/ConnectSnrCaseDialog';
import { AlertConfirmDialog } from '../../components/simple-dialogs/AlertConfirmDialog';
import { ItemList } from '@startuptools/ui/ItemList';
import { authorizationGroupLink, authorizationGroupLinkEdit } from '../../components/Links';
import { useNavigate } from '../../components/Navigate';
import { Route, Routes } from 'react-router-dom';
import { AuthorizationWithRoutes } from './Authorization';

export const authKindText = {
  [GqlAuthKind.Issue]: <Trans id="Share Issue" />,
  [GqlAuthKind.Warrants]: <Trans id="Warrants" />,
  [GqlAuthKind.Convertibles]: <Trans id="Convertibles" />,
  [GqlAuthKind.Bonds]: <Trans id="Convertibles" />,
};

export const AuthorizationsRoutes = () => {
  return (
    <Routes>
      <Route path="" element={<Authorizations />} />
      <Route path=":authorizationGroupId" element={<AuthorizationWithRoutes />}>
        <Route path="*" element={<AuthorizationWithRoutes />} />
      </Route>
    </Routes>
  );
};

const Authorizations = () => {
  const companyId = useCompanyId();
  const { link, navigate } = useNavigate();
  const { i18n } = useLingui();
  const [showSnrCaseConnect, setShowSnrCaseConnect] = useState<{ id: string; fromDate: string } | null>(null);
  const [showDelete, setShowDelete] = useState<{ id: string } | null>(null);
  const {
    data: { authorizationGroups },
  } = useAuthorizationsSuspenseQuery({ variables: { companyId }, fetchPolicy: 'cache-and-network' });
  // eslint-disable-next-line sut/loading-mutation
  const [doCreate] = useCreateAuthorizationGroupMutation({ refetchQueries: [AuthorizationsDocument] });
  // eslint-disable-next-line sut/loading-mutation
  const [doSetSnrCaseId] = useAgSetSnrCaseIdMutation({ refetchQueries: [AuthorizationsDocument] });
  // eslint-disable-next-line sut/loading-mutation
  const [doDelete] = useDeleteAuthorizationGroupMutation({ refetchQueries: [AuthorizationsDocument] });

  const navigateToAg = (id: string) => {
    return navigate(authorizationGroupLinkEdit({ id }));
  };

  const handleCreate = async () => {
    const newAg = await doCreate({ variables: { companyId } });
    if (newAg.data) {
      return navigateToAg(newAg.data.createAuthorizationGroup.id);
    }
  };

  const handleConnectCase = (e: MouseEvent<HTMLElement>, id: string, fromDate: string) => {
    e.stopPropagation();
    e.preventDefault();
    setShowSnrCaseConnect({ id, fromDate });
  };

  return (
    <>
      <Stack gap={3}>
        <Stack direction="row" justifyContent="space-between">
          <Typography level="h2">
            <Trans id="Authorizations" />
          </Typography>
        </Stack>
        <ItemList
          globalActions={[
            {
              label: <Trans id="New Authorization" />,
              onClick: () => handleCreate(),
              buttonProps: { color: 'primary' },
            },
          ]}
          items={authorizationGroups.map(m => ({
            ...m,
            deletable: true,
          }))}
          linkTo={m => link(authorizationGroupLink(m))}
          onDelete={m => setShowDelete(m)}
          columns={[
            {
              header: <Trans id="Decision Date" />,
              render: m => <DateFormat locale={i18n.locale}>{m.meetingConcern?.meeting.startTime}</DateFormat>,
            },
            {
              header: <Trans id="Registration Date" />,
              render: m => <DateFormat locale={i18n.locale}>{m.snrCase?.registrationDate}</DateFormat>,
            },
            {
              header: <Trans id="Kinds" />,
              render: m =>
                m.authorizations.map((a, i) => (
                  <Fragment key={i}>
                    {authKindText[a.kind]}
                    {i < m.authorizations.length - 1 ? ', ' : ''}
                  </Fragment>
                )),
            },
            {
              header: <Trans id="Valid Thru" />,
              render: m => m.validThruAgmYear || <DateFormat locale={i18n.locale}>{m.validThruDate}</DateFormat>,
            },
            {
              header: <Trans id="Case Status" />,
              render: m =>
                m.snrCase ? <Trans id="Registered" /> : <>{!m.meetingConcern ? <Trans id="Draft" /> : '--'}</>,
            },
            {
              header: <Trans id="Case Number" />,
              render: m =>
                m.snrCase ? (
                  <Stack direction="row" gap={1} alignItems="center">
                    {m.snrCase?.filingNumber}/{m.snrCase?.filingYear}
                    <Button
                      variant="outlined"
                      color="neutral"
                      size="sm"
                      onClick={e => {
                        e.stopPropagation();
                        e.preventDefault();
                        return doSetSnrCaseId({ variables: { companyId, input: { id: m.id, snrCaseId: null } } });
                      }}
                    >
                      <Trans id="Disconnect Case" />
                    </Button>
                  </Stack>
                ) : (
                  <>
                    {m.meetingConcern && (
                      <Button
                        variant="outlined"
                        color="neutral"
                        size="sm"
                        onClick={e => handleConnectCase(e, m.id, m.meetingConcern?.meeting.startTime ?? '')}
                      >
                        <Trans id="Connect Case" />
                      </Button>
                    )}
                  </>
                ),
            },
          ]}
        />
      </Stack>
      {showSnrCaseConnect && (
        <ConnectSnrCaseDialog
          subjectKinds={[GqlSnrCaseSubjectKind.Besk, GqlSnrCaseSubjectKind.Beny]}
          onClick={({ id }) => {
            return doSetSnrCaseId({ variables: { companyId, input: { snrCaseId: id, id: showSnrCaseConnect.id } } });
          }}
          onClose={() => setShowSnrCaseConnect(null)}
          fromDate={showSnrCaseConnect.fromDate}
        />
      )}
      {showDelete && (
        <AlertConfirmDialog
          onClose={() => setShowDelete(null)}
          content={<Trans id="Are you sure that you want to delete the authorization?" />}
          onConfirm={async () => {
            await doDelete({ variables: { companyId, id: showDelete.id } });
            setShowDelete(null);
          }}
        />
      )}
    </>
  );
};
